import { updateTask } from '@commenda/api';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { viewUploadedFile } from '../../../utils';

export const SendBylawsForReview = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;
  const { setNotification } = useNotification();

  const uploadDocumentModalVisibility = useToggle();
  const [file, setFile] = useState<FileBasic | null>(null);

  const latestBylawsFileId = (() => {
    const bylawsDoc = serviceRequest?.corporation?.corporationDocuments?.find(
      (d) => d?.documentType === LegacyDocumentType.BylawsUnsigned
    );
    const file = bylawsDoc?.files?.sort((a, b) => b.id - a.id)?.[0];
    return file?.id;
  })();

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadDocumentModalVisibility.show}
                variant="primary"
              >
                <Icons.Upload01 className="size-4 mr-2" />
                Upload Bylaws for Review
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload unsigned bylaws for client review" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Upload01 className="size-4 mr-2" />
                Upload Bylaws for Review
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload unsigned bylaws for client review" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {latestBylawsFileId ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => viewUploadedFile(latestBylawsFileId)}
                    >
                      <Icons.Eye className="size-4 mr-2" />
                      View bylaws
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={uploadDocumentModalVisibility.show}
                  >
                    <Icons.Upload01 className="size-4 mr-2" />
                    Replace bylaws
                  </Button>
                </div>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Bylaws uploaded for client review"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Agent will upload bylaws for your review" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Agent will upload bylaws for your review" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: latestBylawsFileId ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => viewUploadedFile(latestBylawsFileId)}
              >
                <Icons.Eye className="size-4 mr-2" />
                View bylaws
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Agent uploaded bylaws for your review"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        isOpen={uploadDocumentModalVisibility.visible}
        setIsOpen={uploadDocumentModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading="Upload Bylaws for Review"
        documentType={LegacyDocumentType.BylawsUnsigned}
        onUploadSuccess={async () => {
          try {
            const updateServiceRequestRes = await updateTask({
              id: task.id,
              data: { status: TaskStatus.COMPLETED },
            });
            if (updateServiceRequestRes?.ok) {
              await mutate();
              setNotification({
                show: true,
                valid: true,
                title: 'Success',
                message: 'Bylaws uploaded successfully',
              });
            }
          } catch (error) {
            console.error('Failed to update task:', error);
            setNotification({
              show: true,
              valid: false,
              title: 'Error',
              message: 'Failed to upload bylaws',
            });
          }
        }}
      />
    </div>
  );
};
